import { PwInput } from "@/components/ui/pw-input";
import { useCallback, useEffect, useState } from "react";
import { Plus, FileDown, Trash2, CheckCircle2, Pencil, Printer, Package, Ban, History } from "lucide-react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../ui/dialog";
import { accApi, accErr, money } from "./AccountsPanel";
import { VoucherForm } from "./VoucherForm";
import { BillScanButtons } from "./BillScan";
import { printVoucher } from "./InvoicePrint";

const dl = (obj, name) => {
  const a = document.createElement("a");
  a.href = URL.createObjectURL(new Blob([JSON.stringify(obj, null, 2)], { type: "application/json" }));
  a.download = name; a.click();
};
const STATUS = { draft: ["Temporary", "bg-amber-100 text-amber-800"], completed: ["Completed", "bg-emerald-100 text-emerald-800"],
  cancelled: ["Cancelled", "bg-slate-200 text-slate-600 line-through"], posting: ["Posting…", "bg-sky-100 text-sky-800"] };
const CASH_TYPES = [["receipt", "Receipt (money in)"], ["payment", "Payment (money out)"], ["expense", "Expense"], ["contra", "Bank ↔ cash transfer"]];

const CancelDialog = ({ v, onClose, onDone }) => {
  const [pw, setPw] = useState("");
  const [reason, setReason] = useState("");
  const go = () => accApi(`/vouchers/${v.id}/cancel`, { method: "POST", data: { password: pw, reason } })
    .then(() => { toast.success(`${v.number} cancelled — stock returned${v.docs_created ? ", Order Register rows un-linked" : ""}`); onDone(); }).catch(accErr);
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-sm" data-testid="acc-cancel-dialog">
        <DialogHeader><DialogTitle>Cancel {v.number}?</DialogTitle></DialogHeader>
        <input value={reason} onChange={e => setReason(e.target.value)} placeholder="Reason" className="w-full rounded border px-2 py-1.5 text-sm" data-testid="acc-cancel-reason" />
        <PwInput value={pw} onChange={e => setPw(e.target.value)} placeholder="Your password or supervisor password" className="w-full rounded border px-2 py-1.5 text-sm" data-testid="acc-cancel-password" />
        <button onClick={go} className="rounded-lg bg-rose-600 px-3 py-2 text-sm font-semibold text-white" data-testid="acc-cancel-confirm">Cancel invoice</button>
      </DialogContent>
    </Dialog>
  );
};

const AuditDialog = ({ v, onClose }) => {
  const [rows, setRows] = useState(null);
  useEffect(() => { accApi(`/vouchers/${v.id}/audit`).then(setRows).catch(accErr); }, [v.id]);
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-md" data-testid="acc-audit-dialog">
        <DialogHeader><DialogTitle>Change history · {v.number}</DialogTitle></DialogHeader>
        {(rows || []).map(r => <div key={r.id} className="border-b py-1 text-sm"><b className="capitalize">{r.action}</b> by {r.by} <span className="text-xs text-slate-500">({r.via === "supervisor" ? "supervisor password" : "own password"}) · {new Date(r.at).toLocaleString()}</span>{r.reason ? <div className="text-xs">{r.reason}</div> : null}</div>)}
        {rows && !rows.length && <p className="text-sm text-slate-400">No changes after completion.</p>}
      </DialogContent>
    </Dialog>
  );
};

const Actions = ({ v, onEdit, onDel, onCancel, onAudit }) => {
  const b = "inline-flex items-center gap-1 rounded border px-2 py-1 text-xs";
  if (v.status === "draft") return (<>
    <button onClick={() => printVoucher(v.id)} className="p-1.5 text-slate-600" title="Preview print" data-testid={`acc-preview-${v.id}`}><Printer className="w-4 h-4" /></button>
    <button onClick={onEdit} className="p-1.5 text-indigo-600" title="Edit / complete" data-testid={`acc-edit-${v.id}`}><Pencil className="w-4 h-4" /></button>
    <button onClick={onDel} className="p-1.5 text-rose-600" title="Delete draft" data-testid={`acc-del-${v.id}`}><Trash2 className="w-4 h-4" /></button></>);
  if (v.status !== "completed") return v.revision || v.status === "cancelled" ? <button onClick={onAudit} className={b} title="Change history" data-testid={`acc-audit-${v.id}`}><History className="w-3.5 h-3.5" /></button> : null;
  return (<div className="flex flex-wrap justify-end gap-1">
    {v.type === "sale" && <>
      <button onClick={() => printVoucher(v.id)} className={b} data-testid={`acc-print-${v.id}`}><Printer className="w-3.5 h-3.5" /> Invoice</button>
      <button onClick={() => printVoucher(v.id, "slip")} className={b} data-testid={`acc-slip-${v.id}`}><Package className="w-3.5 h-3.5" /> Packing slip</button>
      <button onClick={() => accApi(`/eway/${v.id}`).then(j => dl(j, `eway_${v.number.replace(/\W/g, "_")}.json`)).catch(accErr)} className={b} data-testid={`acc-eway-${v.id}`}><FileDown className="w-3.5 h-3.5" /> E-way</button></>}
    {v.type !== "sale" && v.type !== "purchase" && <CheckCircle2 className="w-4 h-4 text-emerald-600" />}
    <button onClick={onEdit} className={b} title="Edit (password)" data-testid={`acc-revise-${v.id}`}><Pencil className="w-3.5 h-3.5" /></button>
    <button onClick={onCancel} className={`${b} text-rose-600`} title="Cancel (password)" data-testid={`acc-cancel-${v.id}`}><Ban className="w-3.5 h-3.5" /></button>
    {v.revision ? <button onClick={onAudit} className={b} title="Change history" data-testid={`acc-audit-${v.id}`}><History className="w-3.5 h-3.5" /> {v.revision}</button> : null}
  </div>);
};

export const VoucherList = ({ kind, company }) => {
  const [rows, setRows] = useState([]);
  const [edit, setEdit] = useState(null);
  const [cancel, setCancel] = useState(null);
  const [audit, setAudit] = useState(null);
  const [cashType, setCashType] = useState("receipt");
  const type = kind === "cash" ? "" : kind;
  const load = useCallback(() => accApi(`/vouchers?company_id=${company.id}&type=${type}`)
    .then(r => setRows(kind === "cash" ? r.filter(v => CASH_TYPES.some(([t]) => t === v.type)) : r)).catch(accErr), [company.id, type, kind]);
  useEffect(() => { load(); }, [load]);
  const del = (v) => accApi(`/vouchers/${v.id}`, { method: "DELETE" }).then(() => { toast.success("Draft deleted"); load(); }).catch(accErr);
  const openEdit = (v) => accApi(`/vouchers/${v.id}`).then(setEdit).catch(accErr);
  const newType = kind === "cash" ? cashType : kind;
  return (
    <div className="space-y-3" data-testid={`acc-${kind}-list`}>
      <div className="flex flex-wrap items-center gap-2">
        {kind === "cash" && (
          <select value={cashType} onChange={e => setCashType(e.target.value)} className="rounded border px-2 py-1.5 text-sm" data-testid="acc-cash-type">
            {CASH_TYPES.map(([k, l]) => <option key={k} value={k}>{l}</option>)}</select>)}
        <button onClick={() => setEdit({ type: newType })} data-testid={`acc-new-${kind}`}
          className="inline-flex items-center gap-1 rounded-lg px-3 py-2 text-sm font-semibold text-white" style={{ background: "var(--acc)" }}>
          <Plus className="w-4 h-4" /> New {newType === "sale" ? "sales invoice" : newType}</button>
        {kind === "purchase" && <BillScanButtons company={company} onFill={setEdit} />}
      </div>
      <div className="overflow-x-auto rounded-xl border bg-white">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-left text-xs text-slate-500"><tr>
            <th className="px-3 py-2">No.</th><th className="px-3 py-2">Date</th><th className="px-3 py-2">Party</th>
            <th className="px-3 py-2">Type / method</th><th className="px-3 py-2 text-right">Total</th><th className="px-3 py-2">Status</th><th className="px-3 py-2" /></tr></thead>
          <tbody>
            {rows.map(v => {
              const [sl, sc] = STATUS[v.status] || STATUS.draft;
              return (
                <tr key={v.id} className="border-t" data-testid={`acc-voucher-${v.id}`}>
                  <td className="px-3 py-2 font-mono text-xs">{v.number || "—"}</td>
                  <td className="px-3 py-2 whitespace-nowrap">{v.date}</td>
                  <td className="px-3 py-2">{v.party_name}{v.parcels?.length ? <div className="text-[10px] text-slate-500">{v.parcels.length} parcel(s) · from pending orders</div> : null}</td>
                  <td className="px-3 py-2 text-xs">{v.type}{v.supply_type ? ` · ${v.supply_type}` : ""}{v.service_method ? ` · ${v.service_method}` : ""}</td>
                  <td className="px-3 py-2 text-right font-semibold whitespace-nowrap">{money(v.totals?.total, v.currency)}
                    {v.currency && v.currency !== "INR" && <div className="text-[10px] font-normal text-slate-400">{money(v.totals?.total_inr)}</div>}</td>
                  <td className="px-3 py-2">
                    <span className={`rounded-full px-2 py-0.5 text-[11px] font-semibold ${sc}`} data-testid={`acc-status-${v.id}`}>{sl}</span>
                    {v.docs_created && v.status === "completed" && <div className="text-[10px] text-indigo-600">OR rows linked: {v.or_rows_linked}</div>}
                    {v.revision ? <div className="text-[10px] text-amber-700">edited ×{v.revision}</div> : null}
                  </td>
                  <td className="px-3 py-2 whitespace-nowrap text-right">
                    <Actions v={v} onEdit={() => openEdit(v)} onDel={() => del(v)} onCancel={() => setCancel(v)} onAudit={() => setAudit(v)} /></td>
                </tr>);
            })}
            {!rows.length && <tr><td colSpan={7} className="py-8 text-center text-slate-400">Nothing yet</td></tr>}
          </tbody>
        </table>
      </div>
      {edit && <VoucherForm company={company} initial={edit} onClose={() => setEdit(null)} onSaved={() => { setEdit(null); load(); }} />}
      {cancel && <CancelDialog v={cancel} onClose={() => setCancel(null)} onDone={() => { setCancel(null); load(); }} />}
      {audit && <AuditDialog v={audit} onClose={() => setAudit(null)} />}
    </div>
  );
};
